import React, { useRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import ru from "date-fns/locale/ru";
import en from "date-fns/locale/en-US";
import kk from "date-fns/locale/kk";
import zh from "date-fns/locale/zh-CN";
import "./CustomDatePicker.scss";
import cn from "classnames";

registerLocale("ru", ru);
registerLocale("en", en);
registerLocale("kz", kk);
registerLocale("cn", zh);

const CustomDatePicker = ({
  dateFormat,
  placeholderText,
  bgGray = false,
  withBorder = false,
  IconStart = null,
  onChange,
  showYearNavigation = false,
  errorText = "",
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const datePickerRef = useRef(null);

  const currentLocale = i18n.language || "ru";

  const getDateFormat = () => {
    if (dateFormat) return dateFormat;

    switch (currentLocale) {
      case "ru":
      case "kz":
      case "cn":
        return "dd.MM.yyyy";
      case "en":
      default:
        return "MM/dd/yyyy";
    }
  };

  const getPlaceholder = () => {
    if (placeholderText) return placeholderText;

    return t("datePicker.placeholder", {
      defaultValue: currentLocale === "en" ? "MM/DD/YYYY" : "ДД.ММ.ГГГГ",
    });
  };

  const handleRawInput = (e) => {
    if (rest.selectsRange) return;
    if (e.type === "change") {
      const input = e.target.value.replace(/\D/g, "");
      let formattedValue = "";

      const separator = currentLocale === "en" ? "/" : ".";

      if (input.length > 0) {
        if (currentLocale === "en") {
          formattedValue = input.slice(0, 2);
          if (input.length > 2) {
            formattedValue += separator + input.slice(2, 4);
          }
          if (input.length > 4) {
            formattedValue += separator + input.slice(4, 8);
          }
        } else {
          formattedValue = input.slice(0, 2);
          if (input.length > 2) {
            formattedValue += separator + input.slice(2, 4);
          }
          if (input.length > 4) {
            formattedValue += separator + input.slice(4, 8);
          }
        }
      }

      e.target.value = formattedValue;
    }
  };

  const handleWrapperClick = (event) => {
    if (!IconStart || rest.disabled || event.target.closest(".react-datepicker")) return;
    datePickerRef.current?.setOpen(true);
  };

  return (
    <div>
      <div
        className={cn("CustomDatePicker-wrapper", {
          ["CustomDatePicker-wrapper__border"]: withBorder,
          ["CustomDatePicker-wrapper__bgGray"]: bgGray,
          ["CustomDatePicker-wrapper__withIcon"]: IconStart,
        })}
        onClick={handleWrapperClick}
      >
        {IconStart && (
          <div
            className={cn("CustomDatePicker-wrapper__icon", {
              ["CustomDatePicker-wrapper__icon_selected"]: rest.selected || rest.startDate,
            })}
          >
            {IconStart}
          </div>
        )}
        <DatePicker
          ref={datePickerRef}
          dateFormat={getDateFormat()}
          placeholderText={getPlaceholder()}
          locale={currentLocale}
          onChangeRaw={handleRawInput}
          onChange={onChange}
          {...rest}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            decreaseYear,
            increaseYear,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
            prevYearButtonDisabled,
            nextYearButtonDisabled,
          }) => (
            <div className="react-datepicker__header">
              <div className="react-datepicker__current-month">
                {date.toLocaleString(currentLocale, { month: "long" })} {date.getFullYear()}
              </div>
              {/* Кнопки навигации */}
              {showYearNavigation && (
                <button
                  type="button"
                  className="react-datepicker__navigation react-datepicker__navigation--previous react-datepicker__navigation--year"
                  onClick={decreaseYear}
                  disabled={prevYearButtonDisabled}
                >
                  <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous react-datepicker__navigation-icon--double">
                    Previous Year
                  </span>
                </button>
              )}
              <button
                type="button"
                className="react-datepicker__navigation react-datepicker__navigation--previous"
                style={{ left: showYearNavigation ? "70px" : "40px" }}
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
              >
                <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
                  Previous Month
                </span>
              </button>
              <button
                type="button"
                className="react-datepicker__navigation react-datepicker__navigation--next"
                style={{ right: showYearNavigation ? "70px" : "40px" }}
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
              >
                <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
                  Next Month
                </span>
              </button>
              {showYearNavigation && (
                <button
                  type="button"
                  className="react-datepicker__navigation react-datepicker__navigation--next react-datepicker__navigation--year"
                  onClick={increaseYear}
                  disabled={nextYearButtonDisabled}
                >
                  <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next react-datepicker__navigation-icon--double">
                    Next Year
                  </span>
                </button>
              )}
            </div>
          )}
        />
      </div>
      {errorText && (
        <div className="CustomDatePicker-wrapper__errorText" role="alert">
          {errorText}
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
